import React from 'react';
import styled, { keyframes } from 'styled-components';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';

// Shimmer animation
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Base skeleton element with theme-aware gradient
const SkeletonBase = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? `linear-gradient(
          90deg,
          rgba(255, 255, 255, 0.03) 0%,
          rgba(255, 255, 255, 0.08) 50%,
          rgba(255, 255, 255, 0.03) 100%
        )`
      : `linear-gradient(
          90deg,
          rgba(0, 0, 0, 0.04) 0%,
          rgba(0, 0, 0, 0.08) 50%,
          rgba(0, 0, 0, 0.04) 100%
        )`};
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: ${(props) => props.radius || '4px'};
`;

// Page Container
const PageContainer = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.background.primary
      : '#f2f2f2'};
  min-height: 100vh;
  padding: 48px 32px;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// Card Component
const Card = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgba(255,255,255,0.95)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.08)'};
  border-radius: 8px;
  box-shadow: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(0, 0, 0, 0.1) 0px 1px 3px'
      : 'rgba(0, 0, 0, 0.05) 0px 1px 3px'};
  padding: 32px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    padding: 20px;
    margin-bottom: 16px;
  }
`;

// Header Skeleton
const HeaderCard = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const OrderInfoSkeleton = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const IconSkeleton = styled(SkeletonBase)`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  flex-shrink: 0;
`;

const OrderNumberSkeleton = styled(SkeletonBase)`
  height: 32px;
  width: 200px;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    height: 24px;
    width: 150px;
  }
`;

const CustomerNameSkeleton = styled(SkeletonBase)`
  height: 20px;
  width: 150px;
`;

const ButtonSkeleton = styled(SkeletonBase)`
  height: 40px;
  width: 120px;
  border-radius: 8px;
`;

// Main Grid
const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr 0.8fr;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    display: block;
    margin-bottom: 16px;
  }
`;

// Financial Card Skeleton
const FinancialRowSkeleton = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: ${(props) =>
    props.isTotal
      ? 'none'
      : `1px solid ${
          props.theme.name === 'dark'
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(0,0,0,0.05)'
        }`};
  ${(props) =>
    props.isTotal &&
    `
    padding-top: 16px;
    margin-top: 8px;
    border-top: 2px solid ${
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
    };
  `}
`;

const FinancialLabelSkeleton = styled(SkeletonBase)`
  height: 16px;
  width: 80px;
`;

const FinancialValueSkeleton = styled(SkeletonBase)`
  height: 16px;
  width: 60px;
`;

// Event Info Skeleton
const DetailRowSkeleton = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: ${(props) =>
    props.noBorder
      ? 'none'
      : `1px solid ${
          props.theme.name === 'dark'
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(0,0,0,0.05)'
        }`};
`;

const DetailLabelSkeleton = styled(SkeletonBase)`
  height: 14px;
  width: 60px;
`;

const DetailValueSkeleton = styled(SkeletonBase)`
  height: 16px;
  width: 120px;
`;

// Quick Actions Skeleton
const QuickActionsCard = styled(Card)`
  padding: 20px;
  margin-bottom: 0;
`;

const ActionButtonSkeleton = styled(SkeletonBase)`
  height: 40px;
  width: 100%;
  border-radius: 8px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

// Ticket Skeleton
const TicketItemSkeleton = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.05)'};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TicketTitleSkeleton = styled(SkeletonBase)`
  height: 20px;
  width: 250px;
  margin-bottom: 16px;
`;

const TicketDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const TicketDetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TicketLabelSkeleton = styled(SkeletonBase)`
  height: 12px;
  width: 50px;
`;

const TicketValueSkeleton = styled(SkeletonBase)`
  height: 16px;
  width: 80px;
`;

// Timeline Skeleton
const TimelineContainer = styled(Card)`
  padding: 24px;
  margin-bottom: 0;
`;

const TimelineItemSkeleton = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px 0;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 20px;
    top: 52px;
    bottom: -16px;
    width: 2px;
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.05)'
        : 'rgba(0,0,0,0.05)'};
  }
`;

const TimelineDotSkeleton = styled(SkeletonBase)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const TimelineContent = styled.div`
  flex: 1;
`;

const TimelineTitleSkeleton = styled(SkeletonBase)`
  height: 16px;
  width: 150px;
  margin-bottom: 4px;
`;

const TimelineDateSkeleton = styled(SkeletonBase)`
  height: 14px;
  width: 200px;
`;

// Two Column Layout
const TicketsAndTimelineGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    display: block;
    margin-top: 0;
  }
`;

const TransactionDetailSkeleton = () => {
  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Skeleton */}
        <HeaderCard>
          <OrderInfoSkeleton>
            <IconSkeleton />
            <div>
              <OrderNumberSkeleton />
              <CustomerNameSkeleton />
            </div>
          </OrderInfoSkeleton>
          <ButtonSkeleton />
        </HeaderCard>

        {/* Main Grid Skeleton */}
        <MainGrid>
          {/* Financial Summary Skeleton */}
          <Card>
            <SkeletonBase
              style={{ height: '20px', width: '140px', marginBottom: '24px' }}
            />
            <FinancialRowSkeleton>
              <FinancialLabelSkeleton />
              <FinancialValueSkeleton />
            </FinancialRowSkeleton>
            <FinancialRowSkeleton>
              <FinancialLabelSkeleton />
              <FinancialValueSkeleton />
            </FinancialRowSkeleton>
            <FinancialRowSkeleton>
              <FinancialLabelSkeleton />
              <FinancialValueSkeleton />
            </FinancialRowSkeleton>
            <FinancialRowSkeleton>
              <FinancialLabelSkeleton />
              <FinancialValueSkeleton />
            </FinancialRowSkeleton>
            <FinancialRowSkeleton isTotal>
              <FinancialLabelSkeleton
                style={{ width: '50px', height: '20px' }}
              />
              <FinancialValueSkeleton
                style={{ width: '80px', height: '20px' }}
              />
            </FinancialRowSkeleton>
          </Card>

          {/* Event Info Skeleton */}
          <Card>
            <SkeletonBase
              style={{ height: '20px', width: '100px', marginBottom: '24px' }}
            />
            <DetailRowSkeleton>
              <DetailLabelSkeleton />
              <DetailValueSkeleton />
            </DetailRowSkeleton>
            <DetailRowSkeleton>
              <DetailLabelSkeleton />
              <DetailValueSkeleton />
            </DetailRowSkeleton>
            <DetailRowSkeleton>
              <DetailLabelSkeleton />
              <DetailValueSkeleton />
            </DetailRowSkeleton>
            <DetailRowSkeleton>
              <DetailLabelSkeleton />
              <DetailValueSkeleton />
            </DetailRowSkeleton>
            <DetailRowSkeleton noBorder>
              <DetailLabelSkeleton />
              <DetailValueSkeleton />
            </DetailRowSkeleton>
          </Card>

          {/* Quick Actions Skeleton */}
          <QuickActionsCard>
            <SkeletonBase
              style={{ height: '20px', width: '80px', marginBottom: '16px' }}
            />
            <ActionButtonSkeleton />
            <ActionButtonSkeleton />
            <ActionButtonSkeleton />
            <ActionButtonSkeleton />
          </QuickActionsCard>
        </MainGrid>

        {/* Tickets and Timeline Grid */}
        <TicketsAndTimelineGrid>
          {/* Tickets Section Skeleton */}
          <Card style={{ marginBottom: 0 }}>
            <SkeletonBase
              style={{ height: '20px', width: '80px', marginBottom: '24px' }}
            />

            {/* Ticket Items */}
            {[1, 2, 3].map((index) => (
              <TicketItemSkeleton key={index}>
                <TicketTitleSkeleton />
                <TicketDetailsGrid>
                  <TicketDetailItem>
                    <TicketLabelSkeleton />
                    <TicketValueSkeleton />
                  </TicketDetailItem>
                  <TicketDetailItem>
                    <TicketLabelSkeleton />
                    <TicketValueSkeleton />
                  </TicketDetailItem>
                  <TicketDetailItem>
                    <TicketLabelSkeleton />
                    <TicketValueSkeleton />
                  </TicketDetailItem>
                  <TicketDetailItem>
                    <TicketLabelSkeleton />
                    <TicketValueSkeleton />
                  </TicketDetailItem>
                </TicketDetailsGrid>
              </TicketItemSkeleton>
            ))}
          </Card>

          {/* Timeline Section Skeleton */}
          <TimelineContainer>
            <SkeletonBase
              style={{ height: '20px', width: '140px', marginBottom: '24px' }}
            />

            {/* Timeline Items */}
            {[1, 2, 3, 4].map((index) => (
              <TimelineItemSkeleton key={index}>
                <TimelineDotSkeleton />
                <TimelineContent>
                  <TimelineTitleSkeleton />
                  <TimelineDateSkeleton />
                </TimelineContent>
              </TimelineItemSkeleton>
            ))}
          </TimelineContainer>
        </TicketsAndTimelineGrid>
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default TransactionDetailSkeleton;
